import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  useTheme,
  Typography,
  Skeleton
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useWarehouseUtilization } from '../hooks/useDashboardData';
import { Warehouse } from '@mui/icons-material';

export const WarehouseUtilizationChart: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useWarehouseUtilization();

  // Prepare pie chart data
  const chartData = data?.warehouses.map((warehouse, index) => ({
    id: warehouse.warehouseId,
    value: warehouse.totalQuantity,
    label: warehouse.warehouseName,
    color: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ][index % 6]
  })) || [];

  const totalInventory = data?.warehouses.reduce((sum, w) => sum + w.totalQuantity, 0) || 0;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Typography variant="h4" fontWeight={700}>
            Inventory Distribution by Warehouse
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Total: {totalInventory.toLocaleString()} units across {data?.warehouses.length || 0} warehouses
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Skeleton variant="circular" width={200} height={200} />
            <Box sx={{ width: '100%' }}>
              {Array.from(new Array(3)).map((_, index) => (
                <Skeleton key={index} width="100%" sx={{ mb: 1 }} />
              ))}
            </Box>
          </Box>
        ) : error ? (
          <Typography color="error">Failed to load data</Typography>
        ) : data && data.warehouses.length > 0 ? (
          <Box>
            {/* Pie Chart */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <PieChart
                series={[
                  {
                    data: chartData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -10, color: 'gray' },
                    innerRadius: 30,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 5,
                  },
                ]}
                width={350}
                height={250}
                slotProps={{
                  legend: { hidden: true }
                }}
              />
            </Box>

            {/* Warehouse Details List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.warehouses.map((warehouse, index) => {
                const color = chartData[index]?.color || theme.palette.primary.main;
                return (
                  <Box
                    key={warehouse.warehouseId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      borderRadius: theme.general.borderRadiusSm,
                      backgroundColor: theme.colors.alpha.black[5],
                      '&:hover': {
                        backgroundColor: theme.colors.alpha.black[10],
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: color,
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {warehouse.warehouseName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {warehouse.warehouseCode} • {warehouse.uniqueUnits} SKUs
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right', ml: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {warehouse.percentageOfTotal.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {warehouse.totalQuantity.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">No data available</Typography>
        )}
      </CardContent>
    </Card>
  );
};